import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PhotoCamera as CameraIcon,
} from '@mui/icons-material';
import DataService from '../../services/data-service';

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  dropZone: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.dark,
    },
    '&.dragOver': {
      backgroundColor: theme.palette.primary.main + '20',
      borderColor: theme.palette.primary.dark,
    },
  },
  imageSection: {
    marginBottom: theme.spacing(3),
  },
  imageCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  imageMedia: {
    height: 200,
    backgroundSize: 'contain',
  },
  emptyImagePlaceholder: {
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[500],
  },
  hiddenInput: {
    display: 'none',
  },
  photosGrid: {
    marginTop: theme.spacing(2),
  },
  uploadProgress: {
    marginTop: theme.spacing(2),
  },
});

interface ImageData {
  fileNameOrURLPath: string;
  width: number;
  height: number;
}

interface VendorData {
  recordID: string;
  logoImage?: ImageData;
  profileImage?: ImageData;
  listingBackgroundImage?: ImageData;
  photos?: ImageData[];
}

interface ImageTypeConfig {
  label: string;
  instructions: string;
  single: boolean;
}

const IMAGE_TYPES: Record<string, ImageTypeConfig> = {
  logoImage: { label: 'Logo Image', instructions: 'Your company Logo.', single: true },
  profileImage: {
    label: 'Profile Image',
    instructions:
      'A small photo, of a friendly face, used to personalize your listing. Should be square and small (less than 512x512). Will be cropped to a circle.',
    single: true,
  },
  listingBackgroundImage: {
    label: 'Listing Background Image',
    instructions:
      'A vibey image that will be ghosted in the background of your elevated listing.',
    single: true,
  },
  photos: {
    label: 'Photos',
    instructions: 'A photo album to highlight your business.',
    single: false,
  },
};

interface VendorImageUploadOwnProps {
  vendor: VendorData | null;
  onImageUpdate?: (result: unknown) => void;
}

interface VendorImageUploadProps extends VendorImageUploadOwnProps, WithStyles<typeof styles> {}

interface VendorImageUploadState {
  uploading: boolean;
  uploadProgress: Record<string, number>;
  error: string | null;
  success: string | null;
  dragOver: boolean;
  deleteDialogOpen: boolean;
  imageToDelete: { imageType: string; imageUrl: string | null } | null;
}

class VendorImageUpload extends React.Component<
  VendorImageUploadProps,
  VendorImageUploadState
> {
  fileInputRefs: Record<string, React.RefObject<HTMLInputElement | null>>;

  constructor(props: VendorImageUploadProps) {
    super(props);

    this.state = {
      uploading: false,
      uploadProgress: {},
      error: null,
      success: null,
      dragOver: false,
      deleteDialogOpen: false,
      imageToDelete: null,
    };

    this.fileInputRefs = {};
    Object.keys(IMAGE_TYPES).forEach((type) => {
      this.fileInputRefs[type] = React.createRef<HTMLInputElement>();
    });
  }

  handleDragEnter = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: true });
  };

  handleDragLeave = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
  };

  handleDragOver = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleDrop = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      void this.handleFileUpload(files, 'photos');
    }
  };

  handleFileInputChange =
    (imageType: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const files = Array.from(event.target.files ?? []);
      if (files.length > 0) {
        void this.handleFileUpload(files, imageType);
      }
    };

  handleFileUpload = async (files: File[], imageType: string): Promise<void> => {
    this.setState({ uploading: true, error: null, success: null });

    try {
      const dataService = new DataService();
      const { vendor } = this.props;
      const isSingleImage = IMAGE_TYPES[imageType].single;

      const filesToUpload = isSingleImage ? [files[0]] : files;

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];

        if (!this.validateFile(file)) {
          continue;
        }

        this.setState((prevState) => ({
          uploadProgress: {
            ...prevState.uploadProgress,
            [`${imageType}_${i}`]: 0,
          },
        }));

        const base64Data = await this.fileToBase64(file);

        this.setState((prevState) => ({
          uploadProgress: {
            ...prevState.uploadProgress,
            [`${imageType}_${i}`]: 50,
          },
        }));

        const imageInfo = await dataService.uploadVendorImage(
          vendor!.recordID,
          imageType,
          base64Data,
          file.name,
          file.type,
        );

        this.setState((prevState) => ({
          uploadProgress: {
            ...prevState.uploadProgress,
            [`${imageType}_${i}`]: 100,
          },
        }));

        if (this.props.onImageUpdate) {
          this.props.onImageUpdate(imageInfo);
        }
      }

      this.setState({
        success: `Successfully uploaded ${filesToUpload.length} image(s)`,
        uploading: false,
      });

      setTimeout(() => {
        this.setState({ success: null, uploadProgress: {} });
      }, 3000);
    } catch (error) {
      console.error('Error uploading image:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.setState({
        error: `Failed to upload image: ${message}`,
        uploading: false,
      });
    }
  };

  fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (event) => reject(event);
      reader.readAsDataURL(file);
    });
  };

  validateFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      this.setState({ error: 'Please upload only JPEG, PNG, GIF, or WEBP images.' });
      return false;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.setState({ error: 'File size must be less than 5MB.' });
      return false;
    }

    return true;
  };

  handleDeleteImage = async (
    imageType: string,
    imageUrl: string | null = null,
  ): Promise<void> => {
    try {
      const dataService = new DataService();
      const { vendor } = this.props;

      const deleteResult = await dataService.deleteVendorImage(
        vendor!.recordID,
        imageType,
        imageUrl,
      );

      if (this.props.onImageUpdate) {
        this.props.onImageUpdate(deleteResult);
      }

      this.setState({
        success: 'Image deleted successfully',
        deleteDialogOpen: false,
        imageToDelete: null,
      });

      setTimeout(() => {
        this.setState({ success: null });
      }, 3000);
    } catch (error) {
      console.error('Error deleting image:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.setState({
        error: `Failed to delete image: ${message}`,
        deleteDialogOpen: false,
        imageToDelete: null,
      });
    }
  };

  openDeleteDialog = (imageType: string, imageUrl: string | null = null): void => {
    this.setState({
      deleteDialogOpen: true,
      imageToDelete: { imageType, imageUrl },
    });
  };

  closeDeleteDialog = (): void => {
    this.setState({
      deleteDialogOpen: false,
      imageToDelete: null,
    });
  };

  renderSingleImageSection = (imageType: string, image?: ImageData): React.JSX.Element => {
    const { classes } = this.props;
    const { uploading } = this.state;
    const typeConfig = IMAGE_TYPES[imageType];

    return (
      <Grid item xs={12} md={6} key={imageType}>
        <Card className={classes.imageCard}>
          {image ? (
            <CardMedia
              className={classes.imageMedia}
              image={image.fileNameOrURLPath}
              title={typeConfig.label}
            />
          ) : (
            <div className={classes.emptyImagePlaceholder}>
              <ImageIcon fontSize="large" />
            </div>
          )}
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {typeConfig.label}
            </Typography>
            {image ? (
              <Typography variant="body2" color="textSecondary">
                {image.width} x {image.height}px
              </Typography>
            ) : (
              <Typography variant="body1" gutterBottom>
                {typeConfig.instructions}
              </Typography>
            )}
          </CardContent>
          <CardActions>
            <input
              type="file"
              ref={this.fileInputRefs[imageType]}
              onChange={this.handleFileInputChange(imageType)}
              accept="image/*"
              className={classes.hiddenInput}
            />
            <Button
              size="small"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={() => this.fileInputRefs[imageType].current?.click()}
              disabled={uploading}
            >
              {image ? 'Replace' : 'Upload'}
            </Button>
            {image && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => this.openDeleteDialog(imageType)}
                disabled={uploading}
              >
                Delete
              </Button>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  renderPhotosSection = (): React.JSX.Element => {
    const { classes, vendor } = this.props;
    const { uploading } = this.state;
    const photos = vendor?.photos ?? [];

    return (
      <div className={classes.imageSection}>
        <Typography variant="h6" gutterBottom>
          <CameraIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
          Photos ({photos.length})
        </Typography>

        <Paper
          className={`${classes.dropZone} ${this.state.dragOver ? 'dragOver' : ''}`}
          onDragEnter={this.handleDragEnter}
          onDragLeave={this.handleDragLeave}
          onDragOver={this.handleDragOver}
          onDrop={this.handleDrop}
          onClick={() => this.fileInputRefs.photos.current?.click()}
        >
          <UploadIcon fontSize="large" color="primary" />
          <Typography variant="h6" gutterBottom>
            Drag & Drop Photos Here
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Or click to select multiple files
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supports: JPEG, PNG, GIF, WEBP (max 5MB each)
          </Typography>
        </Paper>

        <input
          type="file"
          ref={this.fileInputRefs.photos}
          onChange={this.handleFileInputChange('photos')}
          accept="image/*"
          multiple
          className={classes.hiddenInput}
        />

        {photos.length > 0 && (
          <Grid container spacing={2} className={classes.photosGrid}>
            {photos.map((photo, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardMedia
                    className={classes.imageMedia}
                    image={photo.fileNameOrURLPath}
                    title={`Photo ${index + 1}`}
                  />
                  <CardContent>
                    <Typography variant="body2">
                      {photo.width} x {photo.height}px
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() =>
                        this.openDeleteDialog('photos', photo.fileNameOrURLPath)
                      }
                      disabled={uploading}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    );
  };

  override render(): React.JSX.Element {
    const { classes, vendor } = this.props;
    const { error, success, uploadProgress, deleteDialogOpen, imageToDelete } = this.state;

    if (!vendor) {
      return (
        <Alert severity="warning">No vendor data available for image management.</Alert>
      );
    }

    return (
      <div className={classes.root}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ marginBottom: 2 }}>
            {success}
          </Alert>
        )}

        {Object.keys(uploadProgress).length > 0 && (
          <Box className={classes.uploadProgress}>
            <Typography variant="body2" gutterBottom>
              Upload Progress:
            </Typography>
            {Object.entries(uploadProgress).map(([key, progress]) => (
              <Box key={key} display="flex" alignItems="center" gap={1}>
                <Typography variant="caption">{key}:</Typography>
                <CircularProgress variant="determinate" value={progress} size={20} />
                <Typography variant="caption">{progress}%</Typography>
              </Box>
            ))}
          </Box>
        )}

        <div className={classes.imageSection}>
          <Typography variant="h6" gutterBottom>
            <ImageIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
            Individual Images
          </Typography>
          <Grid container spacing={2}>
            {this.renderSingleImageSection('logoImage', vendor.logoImage)}
            {this.renderSingleImageSection('profileImage', vendor.profileImage)}
            {this.renderSingleImageSection(
              'listingBackgroundImage',
              vendor.listingBackgroundImage,
            )}
          </Grid>
        </div>

        {this.renderPhotosSection()}

        <Dialog open={deleteDialogOpen} onClose={this.closeDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this image? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDeleteDialog}>Cancel</Button>
            <Button
              color="error"
              onClick={() => {
                if (imageToDelete) {
                  void this.handleDeleteImage(
                    imageToDelete.imageType,
                    imageToDelete.imageUrl,
                  );
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(VendorImageUpload);
